// Enemy rendering functions - draws all enemy types

import type p5 from 'p5';
import { getP5 } from '../../../core/p5-instance';
import { getEnemies, getDyingFadeAlpha } from '../../../store/index';
import { ENEMY_CONFIG } from '../../../config/enemy-config';
import type { LaserEnemy, MirrorEnemy, BossEnemy, NormalEnemy, TankEnemy, FastEnemy, ArmoredEnemy, SpeedyEnemy } from '../../../types/game-types';

/**
 * drawDamageSmoke() - ダメージ煙エフェクトの描画
 * HP50%以下の敵に表示される煙エフェクト
 */
function drawDamageSmoke(size: number, hpRatio: number): void {
    const p = getP5();
    const smokeIntensity = p.map(hpRatio, 0, 0.5, 1, 0);
    const smokeCount = p.floor(3 * smokeIntensity);

    p.noStroke();
    for (let i = 0; i < smokeCount; i++) {
        const time = p.frameCount * 0.05 + i * 2;
        const offsetX = p.sin(time + i) * size * 0.3;
        const offsetY = -p.abs(p.sin(time * 0.7)) * size * 0.5 - size * 0.2;
        const smokeSize = size * 0.2 * (1 + p.sin(time) * 0.3);
        const smokeAlpha = 80 * smokeIntensity * (1 - p.abs(p.sin(time * 0.5)));

        p.fill(100, 100, 100, smokeAlpha);
        p.circle(offsetX, offsetY, smokeSize);
    }
}

/**
 * getDamageColor() - ダメージに応じた色を計算
 * HPが減るにつれて暗く赤くなる
 */
export function getDamageColor(baseColor: p5.Color, hpRatio: number): p5.Color {
    const p = getP5();
    let r = p.red(baseColor);
    let g = p.green(baseColor);
    let b = p.blue(baseColor);

    // ダメージが多いほど赤くなり、暗くなる
    const damageAmount = 1 - hpRatio;
    r = r + damageAmount * (255 - r) * 0.5;  // 赤を増やす
    g = g * (0.5 + hpRatio * 0.5);  // 緑を減らす
    b = b * (0.5 + hpRatio * 0.5);  // 青を減らす

    return p.color(r, g, b);
}

/**
 * drawNormalEnemy() - normal敵の描画
 * ダイヤ形状
 */
export function drawNormalEnemy(enemy: NormalEnemy, damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    p.fill(damageColor);
    p.stroke(p.lerpColor(p.color(255), p.color(180, 150, 150), 1 - hpRatio));
    p.strokeWeight(2);

    p.beginShape();
    p.vertex(0, -enemy.size / 2);      // 上
    p.vertex(enemy.size / 2, 0);       // 右
    p.vertex(0, enemy.size / 2);       // 下
    p.vertex(-enemy.size / 2, 0);      // 左
    p.endShape(p.CLOSE);
}

/**
 * drawTankEnemy() - tank敵の描画
 * 六角形 + HPインジケーター
 */
export function drawTankEnemy(enemy: TankEnemy, damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    p.fill(damageColor);
    p.stroke(p.lerpColor(p.color(255), p.color(180, 150, 150), 1 - hpRatio));
    p.strokeWeight(2);

    // 六角形
    p.beginShape();
    for (let i = 0; i < 6; i++) {
        const angle = p.TWO_PI / 6 * i;
        p.vertex(p.cos(angle) * enemy.size / 2, p.sin(angle) * enemy.size / 2);
    }
    p.endShape(p.CLOSE);

    // 装甲ダメージインジケーター：円形
    p.noFill();
    p.stroke(255, 255 * hpRatio, 0, 200);
    p.strokeWeight(3);
    p.arc(0, 0, enemy.size / 2, enemy.size / 2, -p.HALF_PI, -p.HALF_PI + p.TWO_PI * hpRatio);
}

/**
 * drawFastEnemy() - fast敵の描画
 * 三角形
 */
export function drawFastEnemy(enemy: FastEnemy, damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    p.fill(damageColor);
    p.stroke(p.lerpColor(p.color(255), p.color(180, 150, 150), 1 - hpRatio));
    p.strokeWeight(2);

    p.beginShape();
    p.vertex(0, enemy.size / 2);               // 下の頂点
    p.vertex(-enemy.size / 2, -enemy.size / 2); // 左上
    p.vertex(enemy.size / 2, -enemy.size / 2);  // 右上
    p.endShape(p.CLOSE);
}

/**
 * drawArmoredEnemy() - armored敵の描画
 * 八角形 + 装甲マーク
 */
export function drawArmoredEnemy(enemy: ArmoredEnemy, damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    p.fill(damageColor);
    p.stroke(p.lerpColor(p.color(255), p.color(200, 180, 180), 1 - hpRatio));
    p.strokeWeight(3); // 太い枠線で硬さを表現

    // 八角形
    p.beginShape();
    for (let i = 0; i < 8; i++) {
        const angle = p.TWO_PI / 8 * i - p.PI / 8;
        p.vertex(p.cos(angle) * enemy.size / 2, p.sin(angle) * enemy.size / 2);
    }
    p.endShape(p.CLOSE);

    // 装甲マーク（ダメージで薄くなる）
    p.noFill();
    p.stroke(255, 255, 255, 150 * hpRatio);
    p.strokeWeight(2);
    p.circle(0, 0, enemy.size / 3);
}

/**
 * drawSpeedyEnemy() - speedy敵の描画
 */
export function drawSpeedyEnemy(enemy: SpeedyEnemy, damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    p.fill(damageColor);
    p.stroke(p.lerpColor(p.color(255), p.color(180, 150, 150), 1 - hpRatio));
    p.strokeWeight(2);

    // 菱形：スピード敵は細長い
    p.beginShape();
    p.vertex(0, -enemy.size / 1.5);      // 上（長い）
    p.vertex(enemy.size / 3, 0);         // 右（短い）
    p.vertex(0, enemy.size / 1.5);       // 下（長い）
    p.vertex(-enemy.size / 3, 0);        // 左（短い）
    p.endShape(p.CLOSE);

    // スピード線エフェクト（ダメージで薄くなる）
    p.stroke(0, 255, 200, 100 * hpRatio);
    p.strokeWeight(1);
    for (let i = 0; i < 3; i++) {
        const lineY = -enemy.size / 2 + i * 10;
        p.line(-enemy.size / 2 - 10, lineY - 5, -enemy.size / 2, lineY);
        p.line(enemy.size / 2, lineY, enemy.size / 2 + 10, lineY - 5);
    }
}

/**
 * drawLaserEnemy() - laser敵の描画
 */
export function drawLaserEnemy(enemy: LaserEnemy, _damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    // 回転をリセットして正面を向かせる
    p.rotate(-enemy.rotation);

    // チャージ・発射状態に応じた色：ダメージも考慮
    let laserColor = getDamageColor(enemy.color, hpRatio);
    let glowIntensity = 0;

    if (enemy.laserState === 'charging') {
        // チャージ中は点滅
        glowIntensity = p.sin(p.frameCount * 0.3) * 0.5 + 0.5;
        laserColor = p.color(255 * hpRatio, 50 + glowIntensity * 200, 100 + glowIntensity * 150);
    } else if (enemy.laserState === 'firing') {
        // 発射中は明るい
        laserColor = p.color(255, 255, 200);
        glowIntensity = 1;
    }

    // ダメージ煙エフェクト
    if (hpRatio < 0.5) {
        drawDamageSmoke(enemy.size, hpRatio);
    }

    // 砲台本体（六角形）
    p.fill(laserColor);
    p.stroke(p.lerpColor(p.color(255, 100, 150), p.color(200, 80, 100), 1 - hpRatio));
    p.strokeWeight(2);
    p.beginShape();
    for (let i = 0; i < 6; i++) {
        const angle = p.TWO_PI / 6 * i - p.HALF_PI;
        p.vertex(p.cos(angle) * enemy.size / 2, p.sin(angle) * enemy.size / 2);
    }
    p.endShape(p.CLOSE);

    // 砲身：下向き
    p.fill(100 * hpRatio, 50 * hpRatio, 80 * hpRatio);
    p.stroke(255, 100, 150);
    p.strokeWeight(1);
    p.rect(-6, enemy.size / 4, 12, enemy.size / 2, 2);

    // コア：チャージ度合いで光る
    if (glowIntensity > 0) {
        p.fill(255, 255, 200, 200 * glowIntensity);
        p.noStroke();
        p.circle(0, 0, enemy.size / 3 * (0.5 + glowIntensity * 0.5));
    }

    // チャージ中の警告エフェクト（レーザー予告線）
    if (enemy.laserState === 'charging') {
        const warningAlpha = p.sin(p.frameCount * 0.2) * 100 + 100;
        p.stroke(255, 0, 100, warningAlpha);
        p.strokeWeight(2);
        // 敵からプレイヤーエリアまでの警告線
        const warningLength = p.height - enemy.y;
        p.line(0, enemy.size / 2, 0, warningLength);

        // 警告マーク
        p.fill(255, 0, 100, warningAlpha);
        p.noStroke();
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text('!', 0, -enemy.size / 2 - 15);
    }

    // レーザー発射中
    if (enemy.laserState === 'firing') {
        // レーザービーム
        const laserWidth = ENEMY_CONFIG.laser.laserWidth;
        const laserLength = p.height - enemy.y;

        // 外側のグロー
        for (let i = 3; i > 0; i--) {
            p.fill(255, 0, 100, 30);
            p.noStroke();
            p.rect(-laserWidth / 2 - i * 5, enemy.size / 2, laserWidth + i * 10, laserLength);
        }

        // メインビーム
        p.fill(255, 100, 150);
        p.rect(-laserWidth / 2, enemy.size / 2, laserWidth, laserLength);

        // 中心の白い線
        p.fill(255, 200, 220);
        p.rect(-laserWidth / 4, enemy.size / 2, laserWidth / 2, laserLength);
    }
}

/**
 * drawMirrorEnemy() - mirror敵の描画
 * 八角形 + 鏡面エフェクト + 反射マーク
 */
export function drawMirrorEnemy(enemy: MirrorEnemy, _damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    // 回転をリセット：回転しない
    p.rotate(-enemy.rotation);

    // 鏡面効果（きらめき）：ダメージで暗くなる
    const shimmer = (p.sin(p.frameCount * 0.15 + enemy.x * 0.1) * 0.3 + 0.7) * hpRatio;

    // ダメージ煙エフェクト
    if (hpRatio < 0.5) {
        drawDamageSmoke(enemy.size, hpRatio);
    }

    // 外側のオーラ：鏡面反射の雰囲気：ダメージで薄くなる
    p.noFill();
    p.stroke(150 * hpRatio, 200 * hpRatio, 255, 100 * shimmer);
    p.strokeWeight(2);
    p.circle(0, 0, enemy.size * 1.2);

    // メインボディ：八角形、鏡のような形：ダメージで暗く赤くなる
    const baseR = 100 + shimmer * 50;
    const baseG = 150 + shimmer * 50;
    const baseB = 200 + shimmer * 55;
    const darkFactor = 0.75 + hpRatio * 0.25;  // 控えめな暗さ
    p.fill(baseR * darkFactor, baseG * darkFactor, baseB * darkFactor);
    p.stroke(p.lerpColor(p.color(200, 230, 255), p.color(180, 200, 220), 1 - hpRatio));
    p.strokeWeight(2);
    p.beginShape();
    for (let i = 0; i < 8; i++) {
        const angle = p.TWO_PI / 8 * i - p.PI / 8;
        const r = enemy.size / 2;
        p.vertex(p.cos(angle) * r, p.sin(angle) * r);
    }
    p.endShape(p.CLOSE);

    // 中央の反射マーク：ダメージで薄くなる
    p.fill(255, 255, 255, 150 * shimmer * hpRatio);
    p.noStroke();
    // 左右反転の矢印
    p.beginShape();
    p.vertex(-8, -3);
    p.vertex(-4, -8);
    p.vertex(-4, -5);
    p.vertex(4, -5);
    p.vertex(4, -8);
    p.vertex(8, -3);
    p.vertex(4, 2);
    p.vertex(4, -1);
    p.vertex(-4, -1);
    p.vertex(-4, 2);
    p.endShape(p.CLOSE);
}

/**
 * drawBossEnemy() - boss敵の描画
 * 十角形 + オーラ + コア + 目
 */
export function drawBossEnemy(enemy: BossEnemy, _damageColor: p5.Color, hpRatio: number): void {
    const p = getP5();
    // ダメージに応じた色を計算
    const bossColor = getDamageColor(enemy.color, hpRatio);
    const bossInnerColor = getDamageColor(enemy.innerColor || p.color(255, 100, 0), hpRatio);

    // ダメージ煙エフェクト：HP50%以下で表示、ボスは大きめ
    if (hpRatio < 0.5) {
        drawDamageSmoke(enemy.size * 1.5, hpRatio);
    }

    // 外側のオーラ：脈動：ダメージで弱くなる
    const bossPulse = p.sin(p.frameCount * 0.1) * 10;
    p.noFill();
    p.stroke(255, 0, 0, 100 * hpRatio + 50);
    p.strokeWeight(3);
    p.circle(0, 0, enemy.size + bossPulse);

    // メインボディ：十角形
    p.fill(bossColor);
    p.stroke(p.lerpColor(p.color(255, 200, 0), p.color(200, 150, 50), 1 - hpRatio));
    p.strokeWeight(3);
    p.beginShape();
    for (let i = 0; i < 10; i++) {
        const angle = p.TWO_PI / 10 * i - p.HALF_PI;
        const r = i % 2 === 0 ? enemy.size / 2 : enemy.size / 2.5;
        p.vertex(p.cos(angle) * r, p.sin(angle) * r);
    }
    p.endShape(p.CLOSE);

    // 内部のコア：ダメージで暗くなる
    p.fill(bossInnerColor);
    p.noStroke();
    p.circle(0, 0, enemy.size / 3);

    // 目のような模様（ダメージで暗くなる）
    const eyeBrightness = 155 + hpRatio * 100;
    p.fill(eyeBrightness, eyeBrightness, 0);
    p.ellipse(-8, -5, 10, 15);
    p.ellipse(8, -5, 10, 15);
    p.fill(0);
    p.circle(-8, -5, 5);
    p.circle(8, -5, 5);
}

/**
 * drawEnemies() - すべての敵を描画
 * 敵のタイプ別に適切な形状で描画する
 */
export function drawEnemies(): void {
    const p = getP5();
    const fadeAlpha = getDyingFadeAlpha();

    for (const enemy of getEnemies()) {
        const isBoss = enemy.type === 'boss';

        // ボス以外でフェード完了済みなら描画スキップ
        if (!isBoss && fadeAlpha <= 0) {
            continue;
        }

        p.push();

        // フェード処理: p.push()の内側でctx操作（スタック順序を保つ）
        const ctx = p.drawingContext as CanvasRenderingContext2D;
        const needsFade = !isBoss && fadeAlpha < 1;
        if (needsFade) {
            ctx.save();
            ctx.globalAlpha = fadeAlpha;
        }

        p.translate(enemy.x, enemy.y);
        p.rotate(enemy.rotation);

        // HP割合を計算（ダメージビジュアル用）
        const hpRatio = enemy.hp / enemy.maxHp;

        // ダメージに応じた色変化：元の色から赤/暗い色へ
        const damageColor = getDamageColor(enemy.color, hpRatio);

        // 危険警告（プレイヤーに近い時）
        if (enemy.y > p.height - 200) {
            const warningAlpha = p.map(enemy.y, p.height - 200, p.height, 0, 150);
            // 点滅する赤い円
            p.fill(255, 0, 0, warningAlpha * p.abs(p.sin(p.frameCount * 0.2)));
            p.circle(0, 0, enemy.size * 2);
        }

        // 敵のグロー：ダメージで暗くなる
        p.fill(p.red(damageColor), p.green(damageColor), p.blue(damageColor), 50 * hpRatio);
        p.circle(0, 0, enemy.size * 1.3);

        // ダメージ煙エフェクト：HP50%以下で表示
        if (hpRatio < 0.5 && enemy.type !== 'boss') {
            drawDamageSmoke(enemy.size, hpRatio);
        }

        // 敵タイプ別描画
        switch (enemy.type) {
            case 'normal':
                drawNormalEnemy(enemy, damageColor, hpRatio);
                break;
            case 'tank':
                drawTankEnemy(enemy, damageColor, hpRatio);
                break;
            case 'fast':
                drawFastEnemy(enemy, damageColor, hpRatio);
                break;
            case 'armored':
                drawArmoredEnemy(enemy, damageColor, hpRatio);
                break;
            case 'speedy':
                drawSpeedyEnemy(enemy, damageColor, hpRatio);
                break;
            case 'laser':
                drawLaserEnemy(enemy, damageColor, hpRatio);
                break;
            case 'mirror':
                drawMirrorEnemy(enemy, damageColor, hpRatio);
                break;
            case 'boss':
                drawBossEnemy(enemy, damageColor, hpRatio);
                break;
            default:
                // デフォルト（ダイヤ形状）
                drawNormalEnemy(enemy, damageColor, hpRatio);
                break;
        }

        if (needsFade) {
            ctx.restore();
        }

        p.pop();
    }
}
